/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { AppNavbar, AppSideBar, IconItem } from '@t4b/core';
import { ToastContainer } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';
import { History, Location } from 'history';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { Modal, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faBook,
  faChartArea,
  faCogs,
  faDesktop,
  faExchangeAlt,
  faFileLines,
  faHandHoldingDollar,
  faList,
  faNetworkWired,
  faPercent,
  faSignOutAlt,
  faUserPlus,
  faUsers,
  faUserTie,
  faHouse
} from '@fortawesome/free-solid-svg-icons';
import GenUserNavbar from './components/generalUser/GenUserNavbar';
import GenUserTopbar from './components/generalUser/GenUserTopbar';
import { APP_LOGO } from './const/logo.const';
import LocalStorageUtils from './utils/local-storage';
import { logoutUserAsync, removeJwt } from './redux/actions/auth';
import fetchConfigAsync, { fetchLanguageJSONAsync } from './redux/actions/shared/fetchConfig';
import AppRoutes from './components/routes/Routes';
import BottomBarMobile from './components/mobile/BottomBarMobile';
import { PAMMState } from './redux/reducers/rootReducer';

import { setLanguagesMenuStatus } from './redux/actions/setMobileMenusStatus';
import { Investor } from './types/investorTypes';
import { AccountType } from './types/authTypes';

import ToastErrorIcon from './icons/ToastErrorIcon';
import ToastSuccessIcon from './icons/ToastSuccessIcon';
import StorePopupModal from './components/shared/StorePopupModal';
import useShowMobileVersion from './hooks/useShowMobileVersion';
import customerPortalIcon from './icons/customer-portal-icon.svg';
import ScrollToTop from './components/ScrollToTop';
import { ChatBot } from './chatBot/ChatBot';
import { trackEvent } from './utils/trackEvent';
import style from './app.module.css';
import MobileMenu from './components/mobile/MobileMenu';
import { useUser } from './utils/useUser';

const App: React.FC = () => {
  const dispatch: any = useDispatch();
  const history: History = useHistory();
  const location: Location = useLocation();
  const { show: showMobileVersion } = useShowMobileVersion();

  const langJSON = useSelector((state: PAMMState) => state.configReducer.langFile);

  // START "AppNavbar" related
  const api = useSelector((state: PAMMState) => state.configReducer.api);
  const languagesList = useSelector((state: PAMMState) => state.configReducer.languages);
  const copyright: string = useSelector((state: PAMMState) => state.configReducer.copyright);
  const defaultLanguage: string = useSelector((state: PAMMState) => state.configReducer.defaultLanguage);
  const customerPortalLink: string = useSelector((state: PAMMState) => state.configReducer.customerPortalLink);

  const [sidebarHidden, setSidebarHidden] = useState<boolean>(true);
  const isAuthenticated = LocalStorageUtils.getValueFromLocalStorage('isAuthenticated') === 'true';

  const [tokenState, setTokenState] = useState(false);

  const handleSidebarClick = () => setSidebarHidden(!sidebarHidden);

  const currentLang: string | null = LocalStorageUtils.getValueFromLocalStorage('lang');
  // this shouldn't be like this
  const investor: Investor = useSelector((state: PAMMState) => state.investorReducer.investor);

  const closedStorePopupModal = localStorage.getItem('closedStorePopupModal');

  const user = useUser();

  useEffect(() => {
    if (defaultLanguage) {
      dispatch(fetchLanguageJSONAsync(currentLang || defaultLanguage));
    }
  }, [defaultLanguage]);

  const isTokenExpired = (): any => {
    if (Number(LocalStorageUtils.getValueFromLocalStorage('exp')) < Date.now()) {
      setTimeout(() => {
        setTokenState(true);
        isTokenExpired();
      }, 360000);
      return true;
    }

    setTimeout(() => {
      setTokenState(false);
      isTokenExpired();
    }, 360000);
    return false;
  };

  const tokenExpired: boolean = isTokenExpired();
  useEffect(() => {
    if (isAuthenticated && tokenExpired) {
      dispatch(logoutUserAsync(api, history, true));
    }
  }, [tokenState]);

  useEffect(() => {
    dispatch(fetchConfigAsync());
  }, [dispatch]);

  const onLangChange = (langKey: string): void => {
    LocalStorageUtils.putValueToLocalStorage('lang', langKey);
    dispatch(fetchLanguageJSONAsync(langKey));

    if (showMobileVersion) {
      dispatch(setLanguagesMenuStatus(false));
    }
  };

  const signOutFunc = () => {
    dispatch(logoutUserAsync(api, history));
    removeJwt();
  };

  const exitButton: JSX.Element = (
    <Nav.Link key="exitBtn" className="pamm-navbar-exit" onClick={signOutFunc} data-testid="navbar-exit">
      <FontAwesomeIcon size="lg" color="white" icon={faSignOutAlt} />
    </Nav.Link>
  );

  const customerPortalButton: JSX.Element = (
    <>
      <button
        type="button"
        className="pamm-navbar-customer-portal"
        onClick={() => trackEvent('ai_assistant', 'aiassistant_button_click')}
      >
        <ChatBot />
      </button>
      <a
        href={customerPortalLink}
        target="_blank"
        rel="noopener noreferrer"
        className="pamm-navbar-customer-portal"
        onClick={() => trackEvent('customer_portal', 'customerportal_button_click')}
      >
        <img src={customerPortalIcon} alt="customer-portal-icon" />
      </a>
    </>
  );
  // END

  // START "AppSideBar" related
  const [showModal, setShowModal] = useState<boolean>(false);

  const sidebarItems = [
    {
      translateKey: 'tfbhomepage',
      isExternal: false,
      link: '/tfb-homepage',
      icon: new IconItem('48px', faHouse, 'lg', '10px', '#70BBD0'),
      tooltipKey: <FormattedMessage id="tfbhomepage" />
    },
    {
      translateKey: 'dashboard',
      isExternal: false,
      link: '/dashboard',
      icon: new IconItem('48px', faChartArea, 'lg', '10px', '#43A09A'),
      tooltipKey: <FormattedMessage id="dashboard" />
    },
    {
      translateKey: 'investors.title',
      isExternal: false,
      link: '/investors',
      icon: new IconItem('48px', faUsers, 'lg', '10px', '#FFFFFF'),
      tooltipKey: <FormattedMessage id="investors.title" />
    },
    {
      translateKey: 'masters.title',
      isExternal: false,
      link: '/money_managers',
      icon: new IconItem('48px', faHandHoldingDollar, 'lg', '10px', '#FFFFFF'),
      tooltipKey: <FormattedMessage id="masters.title" />
    },
    {
      translateKey: 'admins',
      isExternal: false,
      link: '/admins',
      icon: new IconItem('48px', faUserTie, 'lg', '10px', '#FFFFFF'),
      tooltipKey: <FormattedMessage id="admins" />
    },
    {
      translateKey: 'feeAccounts.title',
      isExternal: false,
      link: '/fee-accounts',
      icon: new IconItem('48px', faPercent, 'lg', '10px', '#FFFFFF'),
      tooltipKey: <FormattedMessage id="feeAccounts.title" />
    },
    {
      translateKey: 'create',
      isExternal: false,
      link: '/create/investor',
      icon: new IconItem('48px', faUserPlus, 'lg', '10px', '#70BBD0'),
      tooltipKey: <FormattedMessage id="create" />
    },
    {
      translateKey: 'investorsAttachDetach',
      isExternal: false,
      link: '/mass-attach/investors',
      icon: new IconItem('48px', faNetworkWired, 'lg', '10px', '#70BBD0'),
      tooltipKey: <FormattedMessage id="investorsAttachDetach" />
    },
    {
      translateKey: 'investors.report',
      isExternal: false,
      link: '/investorsReport',
      icon: new IconItem('48px', faFileLines, 'lg', '10px', '#2C869E'),
      tooltipKey: <FormattedMessage id="investors.report" />
    },
    {
      translateKey: 'orders.title',
      isExternal: false,
      link: '/orders',
      icon: new IconItem('48px', faList, 'lg', '10px', '#2C869E'),
      tooltipKey: <FormattedMessage id="orders.title" />
    },
    {
      translateKey: 'journal',
      isExternal: false,
      link: '/journal',
      icon: new IconItem('48px', faBook, 'lg', '10px', '#2C869E'),
      tooltipKey: <FormattedMessage id="journal" />
    },
    {
      translateKey: 'requestsNomPlur',
      isExternal: false,
      link: '/requests',
      icon: new IconItem('48px', faExchangeAlt, 'lg', '10px', '#2C869E'),
      tooltipKey: <FormattedMessage id="requestsNomPlur" />
    },
    {
      translateKey: 'administrator.monitoring',
      isExternal: false,
      link: '/monitoring',
      icon: new IconItem('48px', faDesktop, 'lg', '10px', '#FFFFFF'),
      tooltipKey: <FormattedMessage id="administrator.monitoring" />
    },
    {
      translateKey: 'settings',
      isExternal: false,
      link: '/settings/common',
      icon: new IconItem('48px', faCogs, 'lg', '10px', '#43A09A'),
      tooltipKey: <FormattedMessage id="settings" />
    }
  ];

  const handleShowModal = () => setShowModal(true);

  const handleCloseModal = () => setShowModal(false);

  const licenceModal = (
    <Modal show={showModal} onHide={handleCloseModal}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id="licenceModal.title" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>3.79 (PAMM 1.12 patch 6)</p>
        <p>{copyright}</p>
      </Modal.Body>
    </Modal>
  );

  const sidebarComponent =
    isAuthenticated && !tokenExpired ? (
      <AppSideBar
        sidebarItems={sidebarItems}
        logo={faBars}
        progressItems={[]}
        sidebarHidden={sidebarHidden}
        currentLocation={location.pathname}
        onLicenceClick={handleShowModal}
        isScrollable
      />
    ) : null;
  // END

  const showSidebar = (): boolean => {
    if (user && isAuthenticated && !tokenExpired) {
      if (user.AccountType !== AccountType.Admin) {
        return false;
      }

      return true;
    }

    return false;
  };

  const showAdminNavbar = (): boolean => {
    if (user && isAuthenticated && !tokenExpired) {
      if (!showMobileVersion && user.AccountType === AccountType.Admin) {
        return true;
      }
      if (showMobileVersion && user.AccountType === AccountType.Admin) {
        return true;
      }
    }

    return false;
  };

  const showGeneralUserNavbar = (): boolean => {
    if (user && isAuthenticated && !tokenExpired) {
      if (!showMobileVersion && user.AccountType !== AccountType.Admin) {
        return true;
      }
    }

    return false;
  };

  const showBottomBar = (): boolean => {
    if (user && isAuthenticated && !tokenExpired) {
      if (showMobileVersion && user.AccountType !== AccountType.Admin) {
        return true;
      }
    }

    return false;
  };

  const getAppStyle = (): string => {
    if ((user && user.AccountType === AccountType.Admin) || !user) {
      return 'm-4 p-2';
    }

    return '';
  };

  const overlayscrollbarsOptions = {
    overflowBehavior: {
      x: 'hidden'
    },
    scrollbars: {
      autoHide: 'scroll',
      autoHideDelay: 1000
    }
  } as OverlayScrollbars.Options;

  const mainClass =
    user === undefined || user.AccountType === AccountType.Investor || user.AccountType === AccountType.Master ? 'main' : '';

  return api && Object.keys(langJSON).length !== 0 && defaultLanguage ? (
    <IntlProvider locale={currentLang || defaultLanguage} messages={langJSON}>
      <ScrollToTop />
      <div className={mainClass}>
        {showAdminNavbar() ? (
          <AppNavbar
            logo={APP_LOGO}
            i18nLangs={languagesList}
            currentLangKey={currentLang || defaultLanguage}
            isAuthenticated={isAuthenticated && !tokenExpired}
            onLangChange={onLangChange}
            onSidebarHandlerClick={handleSidebarClick}
            exitButton={exitButton}
            customerPortalButton={customerPortalLink ? customerPortalButton : undefined}
          />
        ) : null}
        {showBottomBar() ? <BottomBarMobile user={user} /> : null}
        <div className={`d-flex justify-content-center${sidebarHidden ? '' : ' sidebar-show'}`}>
          {showAdminNavbar() && closedStorePopupModal !== 'closed' ? <StorePopupModal /> : null}
          {licenceModal}
          {showSidebar() ? sidebarComponent : null}
          {/* eslint-disable-next-line no-nested-ternary */}
          {(showMobileVersion && !user) ||
          (showMobileVersion && user?.AccountType === 'investor') ||
          (showMobileVersion && user?.AccountType === 'master') ? (
            <>
              <OverlayScrollbarsComponent
                options={overlayscrollbarsOptions}
                style={{ height: 'calc(100vh - 58px)', width: '100%' }}
              >
                <div className={style['wrapper']}>
                  <AppRoutes isAuthenticated={isAuthenticated && !tokenExpired} />
                </div>
              </OverlayScrollbarsComponent>
            </>
          ) : user?.AccountType === 'admin' ? (
            <OverlayScrollbarsComponent className="admin-bg-color" style={{ height: 'calc(100vh - 58px)', width: '100%' }}>
              <div className={getAppStyle()} style={{ minWidth: '1146px' }}>
                <AppRoutes isAuthenticated={isAuthenticated && !tokenExpired} currentLang={currentLang || ''} />
              </div>
            </OverlayScrollbarsComponent>
          ) : (
            <div style={{ width: '100%', display: 'flex', minHeight: '100dvh' }}>
              {showGeneralUserNavbar() ? (
                <>
                  <GenUserNavbar
                    signOutFunc={signOutFunc}
                    currentLang={currentLang || defaultLanguage}
                    languages={languagesList}
                    onLangChange={onLangChange}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 'calc(100% - 444px)',
                      width: '100%',
                      padding: `${isAuthenticated ? '0px 30px 0px 30px' : ''}`
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <GenUserTopbar signOutFunc={signOutFunc} />
                      <AppRoutes isAuthenticated={isAuthenticated && !tokenExpired} />
                    </div>
                    <div className="text-small" style={{ margin: '48px 0px' }}>
                      {copyright}
                    </div>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    minWidth: '77%',
                    width: '100%',
                    padding: `${isAuthenticated ? '0px 30px 0px 30px' : ''}`
                  }}
                >
                  <AppRoutes isAuthenticated={isAuthenticated && !tokenExpired} />
                </div>
              )}
            </div>
          )}
        </div>
        <ToastContainer
          icon={({ theme, type }: any) => (type === 'error' ? <ToastErrorIcon /> : <ToastSuccessIcon />)}
          theme="colored"
        />
        <MobileMenu />
      </div>
    </IntlProvider>
  ) : null;
};

export default App;
